/**
 * CollisionGeometryBuilder — spike investigation for issue #286.
 *
 * Pipeline:
 *   1. Composite per-tile collisionMask bitmaps into a full-map pixel bitmap.
 *   2. Run Marching Squares on the bitmap to extract closed polygon contours.
 *   3. Simplify contours with Douglas-Peucker to reduce vertex count.
 *   4. Return solid polygons (walls) + walkable complement polygon (open space).
 *
 * All coordinates are in world pixels (1 unit = 1 pixel at the map's resolution).
 * For a 32px tile size and 40×30 tile map, the bitmap is 1280×960 pixels.
 */

import type { TilesetConfig } from '../generation/TilesetLoader';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Vec2 { x: number; y: number; }

export interface CollisionGeometry {
  /** Closed polygons outlining each solid (blocked) region. */
  solidPolygons: Vec2[][];
  /**
   * The walkable area expressed as a polygon with holes.
   * Index 0 = outer boundary (whole map rectangle).
   * Index 1+ = holes (solid regions cut out of the walkable space).
   * This is the input format expected by navmesh libraries.
   */
  walkableArea: Vec2[][];
  /** Raw pixel bitmap — 1 = solid, 0 = open. Row-major, width × height. */
  bitmap: Uint8Array;
  bitmapWidth: number;
  bitmapHeight: number;
  /** Diagnostics */
  stats: {
    buildTimeMs: number;
    rawSegmentCount: number;
    rawPolygonCount: number;
    simplifiedPolygonCount: number;
    totalVertices: number;
  };
}

// ── Marching Squares lookup table ─────────────────────────────────────────────
//
// case = (NW ? 8 : 0) | (NE ? 4 : 0) | (SE ? 2 : 0) | (SW ? 1 : 0)
//
// Edges: 0 = N (top), 1 = E (right), 2 = S (bottom), 3 = W (left)
// Each entry is an array of [edgeA, edgeB] pairs — one line segment per pair.
// Saddle cases (5 and 10) emit two segments each.

const MS_SEGMENTS: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  [],                      //  0: all open
  [[3, 2]],                //  1: SW
  [[2, 1]],                //  2: SE
  [[3, 1]],                //  3: SW+SE
  [[0, 1]],                //  4: NE
  [[0, 3], [2, 1]],        //  5: NE+SW (saddle — NW/SE open)
  [[0, 2]],                //  6: NE+SE
  [[0, 3]],                //  7: NE+SE+SW
  [[3, 0]],                //  8: NW
  [[2, 0]],                //  9: NW+SW
  [[3, 2], [1, 0]],        // 10: NW+SE (saddle — NE/SW open)
  [[1, 0]],                // 11: NW+SW+SE
  [[3, 1]],                // 12: NW+NE
  [[2, 1]],                // 13: NW+NE+SW
  [[3, 2]],                // 14: NW+NE+SE
  [],                      // 15: all solid
];

// ── CollisionGeometryBuilder ──────────────────────────────────────────────────

export class CollisionGeometryBuilder {
  private readonly tileSize: number;
  /** Douglas-Peucker epsilon in pixels. Higher = fewer vertices, less accurate. */
  private readonly simplifyEpsilon: number;

  constructor(options: { tileSize?: number; simplifyEpsilon?: number } = {}) {
    this.tileSize = options.tileSize ?? 32;
    this.simplifyEpsilon = options.simplifyEpsilon ?? 1.5;
  }

  /**
   * Full pipeline entry point.
   *
   * @param tileGrid        tiles[row][col] — tile index grid from GeneratedMap.
   * @param walkabilityGrid 0=open, 1=solid — from GeneratedMap.collisionGrid or
   *                        derived from isTileWalkable().
   * @param tilesetConfig   Used to look up per-tile collisionMask data.
   *                        If null, walkabilityGrid drives the bitmap directly
   *                        (one pixel per tile, not 32×32).
   */
  build(
    tileGrid: number[][],
    walkabilityGrid: number[][],
    tilesetConfig: TilesetConfig | null
  ): CollisionGeometry {
    const t0 = performance.now();

    const mapH = walkabilityGrid.length;
    const mapW = walkabilityGrid[0]?.length ?? 0;

    // ── Step 1: composite bitmap ─────────────────────────────────────────────
    const { bitmap, bitmapWidth: bw, bitmapHeight: bh } = tilesetConfig
      ? this.compositeBitmap(tileGrid, walkabilityGrid, tilesetConfig, mapW, mapH)
      : this.bitmapFromGrid(walkabilityGrid, mapW, mapH);

    // ── Step 2: marching squares → raw segments ──────────────────────────────
    const segments = this.marchingSquares(bitmap, bw, bh);
    const rawSegmentCount = segments.length;

    // ── Step 3: stitch segments → closed polygons ────────────────────────────
    const rawPolygons = this.stitchSegments(segments);
    const rawPolygonCount = rawPolygons.length;

    // ── Step 4: simplify ─────────────────────────────────────────────────────
    const solidPolygons = rawPolygons
      .map((poly) => this.douglasPeucker(poly, this.simplifyEpsilon))
      .filter((poly) => poly.length >= 3);

    // ── Step 5: build walkable area (map rect with solid holes) ──────────────
    const mapBoundary: Vec2[] = [
      { x: 0,                  y: 0 },
      { x: bw * this.tileSize, y: 0 },
      { x: bw * this.tileSize, y: bh * this.tileSize },
      { x: 0,                  y: bh * this.tileSize },
    ];
    // When bitmap is pixel-scale, coordinates are already in world pixels.
    // When bitmap is tile-scale (no tilesetConfig), scale up.
    const walkableArea = [mapBoundary, ...solidPolygons];

    const buildTimeMs = performance.now() - t0;
    const totalVertices = solidPolygons.reduce((s, p) => s + p.length, 0);

    return {
      solidPolygons,
      walkableArea,
      bitmap,
      bitmapWidth: bw,
      bitmapHeight: bh,
      stats: {
        buildTimeMs,
        rawSegmentCount,
        rawPolygonCount,
        simplifiedPolygonCount: solidPolygons.length,
        totalVertices,
      },
    };
  }

  // ── Step 1a: composite per-tile masks into a full pixel bitmap ───────────────

  private compositeBitmap(
    tileGrid: number[][],
    walkabilityGrid: number[][],
    config: TilesetConfig,
    mapW: number,
    mapH: number
  ): { bitmap: Uint8Array; bitmapWidth: number; bitmapHeight: number } {
    const bw = mapW * 32;
    const bh = mapH * 32;
    const bitmap = new Uint8Array(bw * bh); // all 0 (open) initially

    for (let row = 0; row < mapH; row++) {
      for (let col = 0; col < mapW; col++) {
        if (walkabilityGrid[row][col] === 0) continue; // tile is walkable — skip

        const tileIndex = tileGrid[row]?.[col] ?? 0;
        const tileDef = config.tiles.find((t) => t.index === tileIndex);

        const mask = tileDef?.collisionMask;

        // Stamp mask into bitmap at tile offset (col*32, row*32)
        const ox = col * 32;
        const oy = row * 32;

        if (mask && mask.length === 32) {
          // Per-pixel mask — 32 uint32 values, bit N of row R = pixel (col=N, row=R)
          for (let py = 0; py < 32; py++) {
            const rowBits = mask[py] >>> 0;
            for (let px = 0; px < 32; px++) {
              if ((rowBits >>> px) & 1) {
                bitmap[(oy + py) * bw + (ox + px)] = 1;
              }
            }
          }
        } else if (tileDef?.collisionShape !== 'none') {
          // No mask — fill the entire tile as solid
          for (let py = 0; py < 32; py++) {
            bitmap.fill(1, (oy + py) * bw + ox, (oy + py) * bw + ox + 32);
          }
        }
        // collisionShape === 'none' → tile stays open (already 0)
      }
    }

    return { bitmap, bitmapWidth: bw, bitmapHeight: bh };
  }

  // ── Step 1b: tile-scale fallback (no tilesetConfig) ─────────────────────────

  private bitmapFromGrid(
    walkabilityGrid: number[][],
    mapW: number,
    mapH: number
  ): { bitmap: Uint8Array; bitmapWidth: number; bitmapHeight: number } {
    const bitmap = new Uint8Array(mapW * mapH);
    for (let row = 0; row < mapH; row++) {
      for (let col = 0; col < mapW; col++) {
        bitmap[row * mapW + col] = walkabilityGrid[row][col] ? 1 : 0;
      }
    }
    return { bitmap, bitmapWidth: mapW, bitmapHeight: mapH };
  }

  // ── Step 2: Marching Squares ─────────────────────────────────────────────────
  //
  // Operates on the WxH bitmap by forming (W-1)x(H-1) squares from adjacent
  // 2×2 pixel blocks. Outputs raw line segments as pairs of Vec2 in bitmap
  // pixel coordinates (not yet scaled to world space).

  private marchingSquares(
    bitmap: Uint8Array,
    w: number,
    h: number
  ): Array<[Vec2, Vec2]> {
    const segments: Array<[Vec2, Vec2]> = [];

    const sample = (x: number, y: number): boolean => {
      if (x < 0 || y < 0 || x >= w || y >= h) return false; // out-of-bounds = open
      return bitmap[y * w + x] !== 0;
    };

    // Edge midpoint in bitmap coords for a cell at (col, row):
    //   N = (col + 0.5, row)
    //   E = (col + 1,   row + 0.5)
    //   S = (col + 0.5, row + 1)
    //   W = (col,       row + 0.5)
    const edgePoint = (col: number, row: number, edge: number): Vec2 => {
      switch (edge) {
        case 0: return { x: col + 0.5, y: row };         // N
        case 1: return { x: col + 1,   y: row + 0.5 };   // E
        case 2: return { x: col + 0.5, y: row + 1 };     // S
        case 3: return { x: col,       y: row + 0.5 };   // W
        default: return { x: col, y: row };
      }
    };

    // Iterate over all (W-1)×(H-1) cells
    for (let row = 0; row < h - 1; row++) {
      for (let col = 0; col < w - 1; col++) {
        const nw = sample(col,     row)     ? 8 : 0;
        const ne = sample(col + 1, row)     ? 4 : 0;
        const se = sample(col + 1, row + 1) ? 2 : 0;
        const sw = sample(col,     row + 1) ? 1 : 0;
        const ms_case = nw | ne | se | sw;

        for (const [eA, eB] of MS_SEGMENTS[ms_case]) {
          segments.push([edgePoint(col, row, eA), edgePoint(col, row, eB)]);
        }
      }
    }

    return segments;
  }

  // ── Step 3: stitch segments → closed polygons ────────────────────────────────
  //
  // Each endpoint in the segment list should appear in exactly 2 segments
  // (guaranteed by marching squares for a closed, finite bitmap). We walk
  // from segment to segment to assemble closed loops.

  private stitchSegments(segments: Array<[Vec2, Vec2]>): Vec2[][] {
    if (segments.length === 0) return [];

    // Key a point to a string for fast lookup
    const key = (p: Vec2): string => `${p.x},${p.y}`;

    // Build adjacency: point key → list of segment indices that use it
    const adj = new Map<string, number[]>();
    for (let i = 0; i < segments.length; i++) {
      const [a, b] = segments[i];
      const ka = key(a);
      const kb = key(b);
      if (!adj.has(ka)) adj.set(ka, []);
      if (!adj.has(kb)) adj.set(kb, []);
      adj.get(ka)!.push(i);
      adj.get(kb)!.push(i);
    }

    const used = new Uint8Array(segments.length);
    const polygons: Vec2[][] = [];

    for (let start = 0; start < segments.length; start++) {
      if (used[start]) continue;

      const loop: Vec2[] = [];
      let segIdx = start;
      let prevPoint: Vec2 | null = null;

      // Walk the loop until we return to the starting segment
      for (let safety = 0; safety < segments.length * 2; safety++) {
        if (used[segIdx]) break;
        used[segIdx] = 1;

        const [a, b] = segments[segIdx];

        // Add the junction vertex (the endpoint that matches prevPoint, or a on the
        // first step). Then walk forward from the other endpoint to find the next segment.
        const addPoint: Vec2 = prevPoint === null
          ? a
          : (key(a) === key(prevPoint) ? a : b);
        const nextPrev: Vec2 = prevPoint === null
          ? b
          : (key(a) === key(prevPoint) ? b : a);

        loop.push(addPoint);
        prevPoint = nextPrev;

        // Find the next segment sharing nextPrev
        const neighbours = adj.get(key(nextPrev)) ?? [];
        const nextSeg = neighbours.find((i) => !used[i]);
        if (nextSeg === undefined) break;
        segIdx = nextSeg;
      }

      if (loop.length >= 3) {
        polygons.push(loop);
      }
    }

    return polygons;
  }

  // ── Step 4: Douglas-Peucker simplification ───────────────────────────────────

  private douglasPeucker(points: Vec2[], epsilon: number): Vec2[] {
    if (points.length <= 2) return points;

    // Find the point with maximum perpendicular distance from the line
    // between the first and last points
    let maxDist = 0;
    let maxIdx = 0;
    const start = points[0];
    const end = points[points.length - 1];

    for (let i = 1; i < points.length - 1; i++) {
      const d = perpendicularDistance(points[i], start, end);
      if (d > maxDist) {
        maxDist = d;
        maxIdx = i;
      }
    }

    if (maxDist > epsilon) {
      const left  = this.douglasPeucker(points.slice(0, maxIdx + 1), epsilon);
      const right = this.douglasPeucker(points.slice(maxIdx), epsilon);
      return [...left.slice(0, -1), ...right];
    }

    return [start, end];
  }
}

// ── Private helpers ───────────────────────────────────────────────────────────

function perpendicularDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(p.x - projX, p.y - projY);
}
